import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { BaseModal } from '@repo/components/modals';
import Intro from './Steps/Intro';
import Contacts from './Steps/ContactsAccess';
import DiskAccess from './Steps/DiskAccess';
import Finish from './Steps/Finish';
import styles from './AppleDeviceIntegration.module.scss';
import { TrackingFn } from '@components/Integrations/utils';
import { useDispatch, useSelector } from 'react-redux';
import { selectIntegrationStatusOnDevice } from '@repo/redux/reducers/user/integrations';
import {
  setIntegrationAuthorization,
  syncDeviceIntegration,
} from '@repo/redux/actions/deviceIntegrations';
import { AppleDeviceIntegrationProvider } from '@repo/utils/models/Integration';
import { AnalyticsConnectionType } from '@repo/utils/types';

const steps = [Intro, Contacts, DiskAccess, Finish];

interface AppleDeviceIntegrationModalBaseProps {
  open: boolean;
  onClose: () => void;
  trackConnection: TrackingFn;
  forceDarkMode?: boolean;
}

interface AppleDeviceIntegrationModalProps extends AppleDeviceIntegrationModalBaseProps {
  provider: AppleDeviceIntegrationProvider;
}

const providerToConnectionType: Record<AppleDeviceIntegrationProvider, AnalyticsConnectionType> = {
  'apple-messages': 'iMessage',
  'apple-phone-log': 'Phone',
};

function AppleDeviceIntegrationModal({
  open = false,
  onClose,
  trackConnection,
  forceDarkMode,
  provider,
}: AppleDeviceIntegrationModalProps) {
  const dispatch = useDispatch();
  const integrationStateOnDevice = useSelector(selectIntegrationStatusOnDevice(provider));
  const [stepComponents, setStepComponents] = useState(steps);
  const [step, setStep] = useState(0);
  const [hasContactsAccess, setHasContactsAccess] = useState(false);
  const [hasDiskAccess, setHasDiskAccess] = useState(false);

  function close() {
    window.localStorage.removeItem('mac_disk_access');
    onClose();
    setStep(0);
  }

  async function checkContactsAccess() {
    if (!integrationStateOnDevice?.supported) {
      setHasContactsAccess(false);
      return;
    }
    window.desktopIntegrations.integration
      .isAuthorized('apple-contacts')
      .then(setHasContactsAccess);
  }

  async function checkDiskAccess(checkStep = false): Promise<boolean> {
    if (!integrationStateOnDevice?.supported) {
      setHasDiskAccess(false);
      return false;
    }

    return window.desktopIntegrations.integration.hasDiskAccess(provider).then((value) => {
      if (checkStep && window.localStorage.getItem('mac_disk_access') === 'true' && value) {
        setStep(2);
      }

      setHasDiskAccess(value);
      return value;
    });
  }

  function setupIntegration() {
    trackConnection({ type: providerToConnectionType[provider] });
    window.desktopIntegrations.integration.authorize(provider).then(() => {
      window.desktopIntegrations.integration.isAuthorized(provider).then((authed) => {
        dispatch(setIntegrationAuthorization({ provider, authed }));
      });
    });
  }

  function next() {
    if (step === stepComponents.length - 1) {
      return close();
    }

    if (step === 2) {
      window.localStorage.removeItem('mac_disk_access');
    }

    setStep(step + 1);
  }

  function prev() {
    if (step === 0) {
      return close();
    }

    setStep(step - 1);
  }

  useEffect(() => {
    checkDiskAccess(true);
    checkContactsAccess();
  }, [open]);

  useEffect(() => {
    if (!open && hasDiskAccess) {
      setStepComponents([Intro, Contacts, Finish]);
    }
  }, [hasDiskAccess]);

  const Step = stepComponents[step];

  return (
    <BaseModal
      centered
      className={cn(styles.wrapper, 'modal-medium')}
      isOpen={open}
      onRequestClose={close}
      forceDarkMode={forceDarkMode}
    >
      <Step
        provider={provider}
        checkDiskAccess={checkDiskAccess}
        checkContactsAccess={checkContactsAccess}
        setupIntegration={setupIntegration}
        performSync={() => syncDeviceIntegration(provider)}
        // @ts-ignore
        syncStatus={integrationStateOnDevice}
        hasContactsAccess={hasContactsAccess}
        hasDiskAccess={hasDiskAccess}
        hasIntegration={integrationStateOnDevice?.enabled}
        next={next}
        prev={prev}
        onClose={close}
        forceDarkMode={forceDarkMode}
      />
    </BaseModal>
  );
}

export function MessagesSetupModal(props: AppleDeviceIntegrationModalBaseProps) {
  return <AppleDeviceIntegrationModal provider="apple-messages" {...props} />;
}

export function PhoneSetupModal(props: AppleDeviceIntegrationModalBaseProps) {
  return <AppleDeviceIntegrationModal provider="apple-phone-log" {...props} />;
}
